import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { actions as mapActions } from 'services/map/map-service';
import { actions as containersActions } from 'services/containers/containers-service';
import { IState } from 'services/root-reducer';
import { EOperationStatus, IOperation } from 'types';
import { getCustomWidgetSettings, getOperation } from 'selectors';
import { connect } from 'react-redux';
import { AutoScrollbars, Preloader } from 'components';

import './style.scss';
import { routes } from 'routes';
import { IObjectSearchResult, IPKKObjectSearchResult, SearchType } from 'services/search/search-reducer';
import { IViewOptions } from 'types/omjs/view';
import { IYandexAddressObject } from 'api/geocoder-yandex';
import { SearchResultItem } from './search-result-item';
import { YandexAddressItem } from './yandex-address-item';
import { LayerResultItem } from './layer-result-item';
import { PkkResultItem } from './pkk-result-item';
import { isObjectListAvailable } from '../categories-config';

export interface ISearchResultsProps extends WithTranslation {
    searchState: IState['search'];
    searchSettings: IViewOptions['publication']['settings']['customWidgets'][0];
    searchOperation: IOperation;
    /** Для мобильной версии */
    onSearchResultClick?:
    (item: IObjectSearchResult | IYandexAddressObject | omjs.IContainer | IPKKObjectSearchResult) => void;
}
export interface ISearchResultsState {

}

const mapStateToProps = (state: IState) => ({
    searchState: state.search,
    searchSettings: getCustomWidgetSettings(state, 'search'),
    searchOperation: getOperation(state, 'SEARCH_OBJECTS_OMS')
});

class SearchResultsComponent extends React.Component<ISearchResultsProps, ISearchResultsState> {
    searchResultsWrapper: HTMLDivElement;
    constructor(props: ISearchResultsProps) {
        super(props);

        this.onSearchResultItemClick = this.onSearchResultItemClick.bind(this);
        this.onYandexSearchResultClick = this.onYandexSearchResultClick.bind(this);
        this.onLayerResultClick = this.onLayerResultClick.bind(this);
    }

    componentDidUpdate(prevProps: ISearchResultsProps, prevState: ISearchResultsState) {
        let { searchState } = this.props;
        let { searchState: prevSearchState } = prevProps;

        if (searchState.search_type !== prevSearchState.search_type) {
            if (this.searchResultsWrapper) {
                this.searchResultsWrapper.scrollTop = 0;
            }
        }
    }

    /**
     * Обработчик события на клик по результату поиска
     */
    onSearchResultItemClick(item: IObjectSearchResult) {
        /* включаем слой */
        containersActions.setContainerSelected({
            id_or_code: item.layer,
            selected: true
        });

        if (this.props.onSearchResultClick) {
            this.props.onSearchResultClick(item);
        }

        /**
         * Открываем окно по объекту.
         * Открывается objectShort чтобы призумится к объекту. У обычного routes.object зума
         * по маунту нету
         */
        routes.redirect(
            routes.objectShort,
            { layerCode: item.layer, objectId: item.orbis_id },
            true
        );
    }

    /** Обработчик событий по клику на результат поиска по геокодеру яндекса */
    onYandexSearchResultClick(item: IYandexAddressObject) {
        let obj = item['GeoObject'];
        let [ pos_lon, pos_lat ] = obj['Point']['pos'].split(' ');
        let pos: [number, number] = omjs.ol.proj.transform(
            [ +pos_lon, +pos_lat ],
            'EPSG:4326',
            'EPSG:3857'
        );

        let [
            lower_lon,
            lower_lat
        ] = obj['boundedBy']['Envelope']['lowerCorner'].split(' ');
        let lower: [number, number] = omjs.ol.proj.transform(
            [ +lower_lon, +lower_lat ],
            'EPSG:4326',
            'EPSG:3857'
        );

        let [
            upper_lon,
            upper_lat
        ] = obj['boundedBy']['Envelope']['upperCorner'].split(' ');
        let upper: [number, number] = omjs.ol.proj.transform(
            [ +upper_lon, +upper_lat ],
            'EPSG:4326',
            'EPSG:3857'
        );

        if (this.props.onSearchResultClick) {
            this.props.onSearchResultClick(item);
        }

        let extent = lower.concat(upper);

        mapActions.placeYaAddressMarker(pos);
        mapActions.fitToExtent(extent);
    }

    /** Обработчик событий по клику на результат поиска по слоям */
    onLayerResultClick(item: omjs.IContainer) {
        /* включаем слой */
        containersActions.setContainerSelected({
            id_or_code: item.code,
            selected: true
        });

        if (this.props.onSearchResultClick) {
            this.props.onSearchResultClick(item);
        }

        if (isObjectListAvailable(item)) {
            routes.redirect(routes.list, { layerCode: item.code }, true);
        }
    }

    /**
     * Обработчик события на клик по результату поиска по слоям ПКК
     */
    onSearchResultPkkClick(item: IPKKObjectSearchResult) {
        mapActions.showCadastreLayer(true);
        mapActions.highlightCadastreGeometry(item.geom);

        const geom = new omjs.ol.format.GeoJSON().readGeometry(item.geom);
        mapActions.fitToExtent(geom.getExtent());

        if (this.props.onSearchResultClick) {
            this.props.onSearchResultClick(item);
        }
    }

    /**
     * Сортирует результаты поиска по объектам
     */
    formatObjectsSearchResult(
        search_result: IObjectSearchResult[]
    ): IObjectSearchResult[] {
        let sortLayers = [
            'ia138',
            'parks_1',
            'parks_2',
            'parks_3',
            'sites_1',
            'sites_2',
            'sites_3',
            'projects_1',
            'projects_2',
            'projects_3',
            'programs_1',
            'programs_2',
            'programs_3',
            'programs_4',
            'programs_5',
            'programs_6'
        ].reverse();

        return search_result.sort((a, b) => {
            let i_a = sortLayers.indexOf(a.layer);
            let i_b = sortLayers.indexOf(b.layer);

            return (i_a === i_b) ? (a.name.localeCompare(b.name)) : (i_b - i_a);
        });
    }

    renderNoResult() {
        let { t } = this.props;
        return (
            <div className="no-result-wrapper">
                {t('По вашему запросу ничего не найдено')}
            </div>
        );
    }

    renderPreloader() {
        return (
            <div className="preloader-wrapper">
                <Preloader />
            </div>
        );
    }

    renderResult() {
        let { searchOperation, searchState, searchSettings, t } = this.props;
        if (searchOperation.status === EOperationStatus.SUCCESS) {
            if (searchState.objects_search_result.length === 0) {
                return this.renderNoResult();
            } else {
                if (searchState.search_type === SearchType.Objects) {
                    let items = searchState.objects_search_result;
                    if ((items[0] as IPKKObjectSearchResult)?.cadastral_number) {
                        return (items as IPKKObjectSearchResult[]).map(r => (
                            <PkkResultItem
                                item={r}
                                key={r.cadastral_number}
                                onClick={(item) => this.onSearchResultPkkClick(item)}
                            />
                        ));
                    } else {
                        return (items as IObjectSearchResult[]).map(r => (
                            <SearchResultItem
                                item={r}
                                // С поиска возвращаются одинаковые объекты с одних и тех же слоев
                                // поэтому тут такой key.
                                key={`${r.orbis_id}_${r.layer}_${r.search_column}`}
                                onClick={this.onSearchResultItemClick}
                            />
                        ));
                    }
                } else if (searchState.search_type === SearchType.Address) {
                    if (searchSettings?.values?.searchYandexApi) {
                        let items = searchState.objects_search_result as IYandexAddressObject[];
                        return items.map((r, i) => (
                            <YandexAddressItem
                                item={r}
                                onClick={this.onYandexSearchResultClick}
                                /* eslint-disable-next-line */
                                key={i}
                            />
                        ));
                    } else {
                        return null;
                        // throw new Error('Not implemented');
                    }
                } else if (searchState.search_type === SearchType.Layers) {
                    let items = searchState.objects_search_result as omjs.IContainer[];
                    return items.map((r, i) => (
                        <LayerResultItem
                            item={r}
                            onClick={this.onLayerResultClick}
                            key={r.code}
                        />
                    ));
                } else if (searchState.search_type === SearchType.PKK) {
                    let items = searchState.objects_search_result as IPKKObjectSearchResult[];
                    return items.map(r => (
                        <PkkResultItem
                            item={r}
                            key={r.cadastral_number}
                            onClick={(item) => this.onSearchResultPkkClick(item)}
                        />
                    ));
                } else {
                    return null;
                }
            }
        } else if (searchOperation.status === EOperationStatus.UNKNOWN) {
            // ничего не рендерим если запрос еще не делалася
            return null;
        } else if (searchOperation.error?.status === 404) {
            return this.renderNoResult();
        } else {
            return (
                <div className="error">
                    {t('Ошибка загрузки')}
                </div>
            );
        }
    }

    render() {
        let { searchOperation } = this.props;

        if (searchOperation === null) {
            return null;
        } else if (searchOperation.status === EOperationStatus.UNKNOWN) {
            return null;
        }

        return (
            <div
                className="search-results-wrapper"
                ref={(el) => { this.searchResultsWrapper = el; }}
            >
                <AutoScrollbars>
                    {
                        searchOperation.status === EOperationStatus.PENDING
                            ? this.renderPreloader()
                            : this.renderResult()
                    }
                </AutoScrollbars>
            </div>
        );
    }
}

export const SearchResults = connect(mapStateToProps)(withTranslation()(SearchResultsComponent));
